import * as THREE from 'three';
import { easeInOutCubic } from '../animation/easings';
import type { ResponsiveValues } from '../../types';

// ⚡ MEMORY: Pre-allocated objects - never create inside animation loops
const _spiralPos = new THREE.Vector3();
const _dispersePos = new THREE.Vector3();
const _currentPos = new THREE.Vector3();

interface SpiralProps {
  spiralX: number;
  spiralY: number;
  spiralZ: number;
  spiralRotY: number;
  spiralScale: number;
}

export const calculateSpiralPosition = (
  _index: number,
  angle: number,
  values: ResponsiveValues
): [number, number, number] => {
  const radius = values.radius;
  const heightLoop = values.heightLoop;

  const spiralX = Math.sin(angle * 2) * radius;
  const spiralZ = Math.cos(angle * 2) * radius;
  const spiralY =
    (((angle / (Math.PI * 2)) * heightLoop) % heightLoop) - heightLoop / 2;

  return [spiralX, spiralY, spiralZ];
};

export const updateItemPosition = (
  item: THREE.Object3D,
  index: number,
  selectedIndex: number | null,
  spiralProps: SpiralProps,
  transitionProgress: number,
  values: ResponsiveValues,
  _viewport: { width: number; height: number; factor: number },
  _url: string
) => {
  const t = easeInOutCubic(transitionProgress);
  const { spiralX, spiralY, spiralZ, spiralRotY, spiralScale } = spiralProps;

  _spiralPos.set(spiralX, spiralY, spiralZ);

  if (selectedIndex === index) {
    item.position.lerpVectors(item.position, _spiralPos, Math.max(t, 0.12));

    const currentRotation = THREE.MathUtils.euclideanModulo(
      item.rotation.y,
      Math.PI * 2
    );
    let targetRotation = THREE.MathUtils.euclideanModulo(
      spiralRotY,
      Math.PI * 2
    );

    if (Math.abs(targetRotation - currentRotation) > Math.PI) {
      if (targetRotation > currentRotation) {
        targetRotation -= Math.PI * 2;
      } else {
        targetRotation += Math.PI * 2;
      }
    }

    item.rotation.y = THREE.MathUtils.lerp(currentRotation, targetRotation, t);

    const currentScale = item.scale.x;
    const targetScale = THREE.MathUtils.lerp(spiralScale, spiralScale * 1.04, t);
    item.scale.setScalar(
      THREE.MathUtils.lerp(currentScale, targetScale, Math.max(t, 0.12))
    );
  } else if (selectedIndex !== null) {
    const angle = (index * Math.PI * 2) / values.projectsPerLoop;
    const radius = 15;
    _dispersePos.set(
      Math.cos(angle) * radius,
      Math.sin(angle) * radius,
      15
    );

    item.position.lerpVectors(_spiralPos, _dispersePos, t);
    item.rotation.y = spiralRotY;

    const targetScale = THREE.MathUtils.lerp(spiralScale, spiralScale * 0.8, t);
    item.scale.setScalar(targetScale);
  } else {
    _currentPos.copy(item.position);
    item.position.lerpVectors(_currentPos, _spiralPos, 1 - t);

    // Normaliser la rotation de retour
    const currentRotation = THREE.MathUtils.euclideanModulo(
      item.rotation.y,
      Math.PI * 2
    );
    let targetRotation = THREE.MathUtils.euclideanModulo(
      spiralRotY,
      Math.PI * 2
    );

    if (Math.abs(targetRotation - currentRotation) > Math.PI) {
      if (targetRotation > currentRotation) {
        targetRotation -= Math.PI * 2;
      } else {
        targetRotation += Math.PI * 2;
      }
    }

    item.rotation.y = THREE.MathUtils.lerp(
      currentRotation,
      targetRotation,
      1 - t
    );

    const currentScale = item.scale.x;
    const targetScale = spiralScale;
    item.scale.setScalar(
      THREE.MathUtils.lerp(currentScale, targetScale, 1 - t)
    );
  }
};
